'use client';

import { useCallback } from 'react';
import { useStore } from 'zustand';

import { toLegacySearchFilterParams } from '@/app/(main)/(pages)/busca/[slug]/_utils/legacy-search-filter-params.util';
import { useAgendamentoNavigation } from '@/hooks/navigation/use-agendamento-navigation.hook';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { userLocationStore } from '@/store/user-location.store';
import { getUserLocationStorage } from '@/storage/location.storage';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { slugify } from '@/utils/formatters/slugfy';

const PERSISTED_PARAMS = Object.values(PersistedUserNavigationSearchParams);

const toCitySlug = (city?: string | null, state?: string | null): string | undefined =>
  city && state ? slugify(`${city}-${state}`) : undefined;

const normalizeCitySlug = (value?: string | null): string | undefined =>
  value ? slugify(value) : undefined;

export function useLegacySearchRedirectUrl() {
  const storeLocation = useStore(userLocationStore, (state) => state.userLocation);
  const { buildLink: buildAgendamentoLink } = useAgendamentoNavigation();
  const searchParams = useSearchParams(PERSISTED_PARAMS);
  const cityParam = searchParams.getParam(PersistedUserNavigationSearchParams.CIDADE);

  return useCallback(
    (slug: string, filters?: Record<string, string | string[]>): string => {
      const userLocation = storeLocation ?? getUserLocationStorage() ?? undefined;

      const citySlug = normalizeCitySlug(cityParam) ?? toCitySlug(userLocation?.city, userLocation?.state);
      const overridesCityParams = citySlug
        ? { [PersistedUserNavigationSearchParams.CIDADE]: citySlug }
        : undefined;

      const persistLocationJson = userLocation?.formattedAddress
        ? { [PersistedUserNavigationSearchParams.LOCATION]: JSON.stringify(userLocation) }
        : undefined;

      const overrides = { ...overridesCityParams, ...persistLocationJson };

      return buildAgendamentoLink({
        path: `/busca/${slug}/`,
        query: filters && toLegacySearchFilterParams(filters),
        overrides,
      });
    },
    [cityParam, storeLocation, buildAgendamentoLink],
  );
}
